import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { useAuth, useCurrentUser } from '@/auth/context'
import { Button } from '@/ui/Button'
import { useToast } from '@/ui/toast-context'

/**
 * An unconfirmed address, and the one button that fixes it.
 *
 * Shown on the overview rather than buried in settings because an unconfirmed
 * address is the reason digests, approval requests and password resets silently
 * never arrive — a failure that presents to the user as "AugRank does not send
 * me anything" and to support as a mystery.
 *
 * `verification_email_sent: false` is a real answer, not an error: the account
 * exists and the message could not be handed to SES. It gets its own warning
 * toast, because a success toast over a message that never went is worse than
 * saying nothing.
 *
 * Nothing renders for an account with no address at all — §4 permits that for a
 * mobile signup, and there is no address to confirm.
 */
export function AccountStatus(): JSX.Element | null {
  const { user } = useCurrentUser()
  const { refresh } = useAuth()
  const toast = useToast()

  const resend = useMutation(() => call(() => api.POST('/api/v1/auth/email/resend', {})))

  if (!user.email || user.email_verified) return null

  return (
    <div className="ar-alert ar-alert--warning" role="status">
      <span className="ar-alert__icon" aria-hidden="true">
        !
      </span>
      <div className="ar-alert__body">
        <strong>Confirm {user.email}</strong>
        Approval requests, digests and password resets go to this address, and none of them are sent
        until it is confirmed.
      </div>
      <Button
        tone="ghost"
        size="sm"
        busy={resend.pending}
        busyLabel="Sending"
        onClick={() => {
          void (async () => {
            const result = await resend.run(undefined)
            if (!result.ok) {
              toast.show({
                kind: 'danger',
                title: 'Could not send the link',
                body: result.failure.message,
              })
              return
            }
            if (result.data.verification_email_sent) {
              toast.show({
                kind: 'success',
                title: 'Confirmation link sent',
                body: `Check ${user.email ?? 'your inbox'}. It works once and expires.`,
              })
            } else {
              toast.show({
                kind: 'warning',
                title: 'The link could not be sent just now',
                body: 'Your account is fine. Try again in a few minutes.',
              })
            }
            await refresh()
          })()
        }}
      >
        Send a new link
      </Button>
    </div>
  )
}
